import { useCallback, useEffect, useState, useRef } from "react";
import { useDropzone, FileRejection } from "react-dropzone";
import { useTranslation } from "react-i18next";
import {
  extractTeacherDocumentContent,
  generatePresignedUrl,
  generatePresignedUrlOnly,
  getTeacherDocument,
} from "@packages/api/madlenAPI";
import {
  CheckCircle2,
  File,
  Loader2,
  Upload,
  X,
  XCircle,
  FileText,
} from "lucide-react";
import { Text } from "@core/text";
import {
  PresignedUrlResponseDto,
  TeacherDocumentResponseDto,
} from "@packages/api/madlenAPI.schemas";

export interface FileUploaderProps {
  /**
   * Callback function that will be called when a file is successfully uploaded
   */
  onUploadSuccess?: (
    url: string,
    response?: TeacherDocumentResponseDto,
    downloadUrl?: string,
  ) => void;
  /**
   * Callback function that will be called when an error occurs during upload
   */
  onUploadError?: (error: string) => void;
  /**
   * Callback function that will be called when upload progress changes
   */
  onUploadProgress?: (progress: number) => void;
  /**
   * Callback function that will be called when upload progress changes
   */
  onUploadProgressMultiple?: (progress: number[]) => void;
  /**
   * Callback function that will be called when a file is deleted
   */
  onFileDelete?: (fileId: number) => void;
  /**
   * Maximum file size in bytes (default: 15MB)
   */
  maxSize?: number;
  /**
   * Whether to allow multiple file uploads (default: false)
   */
  multiple?: boolean;
  /**
   * Accepted file types (default: PDF only)
   */
  accept?: Record<string, string[]>;
  /**
   * Whether to extract content from the uploaded file (default: false)
   */
  shouldExtractContent?: boolean;
  /**
   * Custom class name for the container
   */
  className?: string;
  /**
   * Custom styles for the container
   */
  style?: React.CSSProperties;
  /**
   * Custom drag active text
   */
  dragActiveText?: string;
  /**
   * Custom drag inactive text
   */
  dragInactiveText?: string;
  /**
   * Custom uploading text
   */
  uploadingText?: string;
  /**
   * Custom upload success text
   */
  uploadSuccessText?: string;
  /**
   * Custom extracting content text
   */
  extractingContentText?: string;
  /**
   * Color variant for the upload progress and hover states (default: blue)
   */
  uploadColor?: ColorVariant;
  /**
   * Color variant for the extraction progress state (default: blue)
   */
  extractColor?: ColorVariant;
  /**
   * Color variant for the success state (default: green)
   */
  successColor?: ColorVariant;
  /**
   * Whether to use small size (default: false)
   */
  small?: boolean;
  /**
   * Callback function that will be called when a file upload starts
   */
  onUploadStart?: () => void;
  /**
   * Callback function that will be called when all files have finished uploading
   */
  onAllUploadsComplete?: (files: TeacherDocumentResponseDto[]) => void;
  /**
   * Whether to only generate presigned url (default: false)
   */
  shouldOnlyGeneratePresignedUrl?: boolean;
}

const colorMap = {
  blue: {
    border: "border-blue-500 dark:border-blue-400",
    bg: "bg-blue-50 dark:bg-blue-900/20",
    bgLight: "bg-blue-100 dark:bg-blue-900/30",
    text: "text-blue-500 dark:text-blue-400",
    hover:
      "hover:border-blue-500/50 hover:bg-blue-50/50 dark:hover:border-blue-400/50 dark:hover:bg-blue-900/10",
  },
  indigo: {
    border: "border-indigo-500 dark:border-indigo-400",
    bg: "bg-indigo-50 dark:bg-indigo-900/20",
    bgLight: "bg-indigo-100 dark:bg-indigo-900/30",
    text: "text-indigo-500 dark:text-indigo-400",
    hover:
      "hover:border-indigo-500/50 hover:bg-indigo-50/50 dark:hover:border-indigo-400/50 dark:hover:bg-indigo-900/10",
  },
  purple: {
    border: "border-purple-500 dark:border-purple-400",
    bg: "bg-purple-50 dark:bg-purple-900/20",
    bgLight: "bg-purple-100 dark:bg-purple-900/30",
    text: "text-purple-500 dark:text-purple-400",
    hover:
      "hover:border-purple-500/50 hover:bg-purple-50/50 dark:hover:border-purple-400/50 dark:hover:bg-purple-900/10",
  },
  emerald: {
    border: "border-emerald-500 dark:border-emerald-400",
    bg: "bg-emerald-50 dark:bg-emerald-900/20",
    bgLight: "bg-emerald-100 dark:bg-emerald-900/30",
    text: "text-emerald-500 dark:text-emerald-400",
    hover:
      "hover:border-emerald-500/50 hover:bg-emerald-50/50 dark:hover:border-emerald-400/50 dark:hover:bg-emerald-900/10",
  },
  green: {
    border: "border-green-500 dark:border-green-400",
    bg: "bg-green-50 dark:bg-green-900/20",
    bgLight: "bg-green-100 dark:bg-green-900/30",
    text: "text-green-500 dark:text-green-400",
    hover:
      "hover:border-green-500/50 hover:bg-green-50/50 dark:hover:border-green-400/50 dark:hover:bg-green-900/10",
  },
  red: {
    border: "border-red-500 dark:border-red-400",
    bg: "bg-red-50 dark:bg-red-900/20",
    bgLight: "bg-red-100 dark:bg-red-900/30",
    text: "text-red-500 dark:text-red-400",
    hover:
      "hover:border-red-500/50 hover:bg-red-50/50 dark:hover:border-red-400/50 dark:hover:bg-red-900/10",
  },
  yellow: {
    border: "border-yellow-500 dark:border-yellow-400",
    bg: "bg-yellow-50 dark:bg-yellow-900/20",
    bgLight: "bg-yellow-100 dark:bg-yellow-900/30",
    text: "text-yellow-500 dark:text-yellow-400",
    hover:
      "hover:border-yellow-500/50 hover:bg-yellow-50/50 dark:hover:border-yellow-400/50 dark:hover:bg-yellow-900/10",
  },
  orange: {
    border: "border-orange-500 dark:border-orange-400",
    bg: "bg-orange-50 dark:bg-orange-900/20",
    bgLight: "bg-orange-100 dark:bg-orange-900/30",
    text: "text-orange-500 dark:text-orange-400",
    hover:
      "hover:border-orange-500/50 hover:bg-orange-50/50 dark:hover:border-orange-400/50 dark:hover:bg-orange-900/10",
  },
} as const;

export type ColorVariant = keyof typeof colorMap;

export function FileUploader({
  onUploadSuccess,
  onUploadError,
  onUploadProgress,
  onUploadProgressMultiple,
  onFileDelete,
  maxSize = 500 * 1024 * 1024, // 500MB
  multiple = false,
  accept = {
    "application/pdf": [".pdf"],
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [
      ".docx",
    ],
    "text/plain": [".txt"],
    "image/png": [".png"],
    "image/jpeg": [".jpg", ".jpeg", ".JPG", ".JPEG"],
  },
  shouldExtractContent = true,
  shouldOnlyGeneratePresignedUrl = false,
  className = "",
  style,
  dragActiveText,
  dragInactiveText,
  uploadingText,
  uploadSuccessText,
  extractingContentText,
  uploadColor = "blue",
  extractColor = "blue",
  successColor = "green",
  small = false,
  onUploadStart,
  onAllUploadsComplete,
}: FileUploaderProps) {
  const { t } = useTranslation();
  const [isUploading, setIsUploading] = useState<{ [key: number]: string }>({});
  const [isExtracting, setIsExtracting] = useState<{ [key: number]: boolean }>(
    {},
  );
  const [uploadProgress, setUploadProgress] = useState<{
    [key: number]: number;
  }>({});
  const [uploadedFile, setUploadedFile] = useState<{
    [key: number]: TeacherDocumentResponseDto;
  }>({});
  const xhrRefs = useRef<{ [key: number]: XMLHttpRequest }>({});

  const getColorClasses = useCallback(
    (state: "drag-active" | "success" | "default") => {
      switch (state) {
        case "drag-active":
          return `${colorMap[uploadColor as ColorVariant].border} ${colorMap[uploadColor as ColorVariant].bg}`;
        case "success":
          return `${colorMap[successColor as ColorVariant].border} ${colorMap[successColor as ColorVariant].bg}`;
        default:
          return `border-gray-300 dark:border-gray-600 ${colorMap[uploadColor as ColorVariant].hover}`;
      }
    },
    [uploadColor, successColor],
  );

  useEffect(() => {
    if (multiple) {
      onUploadProgressMultiple?.(Object.values(uploadProgress));
    } else {
      onUploadProgress?.(Object.values(uploadProgress)[0]);
    }
  }, [uploadProgress, onUploadProgress, onUploadProgressMultiple, multiple]);

  const startPolling = useCallback(
    async (id: number, url: string, resolve: () => void) => {
      const interval = setInterval(async () => {
        const response = await getTeacherDocument(id);
        if (response.status === "READY") {
          const fileUrl = url.split("?")[0];
          setIsExtracting((prev) => ({
            ...prev,
            [id]: false,
          }));
          setUploadedFile((prev) => ({
            ...prev,
            [id]: response,
          }));
          await onUploadSuccess?.(fileUrl, response);
          resolve();
          clearInterval(interval);
        }
      }, 1000);
      return () => clearInterval(interval);
    },
    [],
  );

  const handleDelete = useCallback(
    (fileId: number) => {
      if (isUploading[fileId]) {
        // Cancel upload if in progress
        if (xhrRefs.current[fileId]) {
          xhrRefs.current[fileId].abort();
          delete xhrRefs.current[fileId];
        }

        // Clean up states
        setIsUploading((prev) => {
          const newState = { ...prev };
          delete newState[fileId];
          return newState;
        });
        setIsExtracting((prev) => {
          const newState = { ...prev };
          delete newState[fileId];
          return newState;
        });
        setUploadProgress((prev) => {
          const newState = { ...prev };
          delete newState[fileId];
          return newState;
        });
        // Don't show error on cancellation
      } else if (uploadedFile[fileId]) {
        // Remove uploaded file
        setIsUploading((prev) => {
          const newState = { ...prev };
          delete newState[fileId];
          return newState;
        });
        setUploadProgress((prev) => {
          const newState = { ...prev };
          delete newState[fileId];
          return newState;
        });
        setIsExtracting((prev) => {
          const newState = { ...prev };
          delete newState[fileId];
          return newState;
        });
        setUploadedFile((prev) => {
          const newState = { ...prev };
          delete newState[fileId];
          return newState;
        });
        onFileDelete?.(fileId);
      }
    },
    [isUploading, uploadedFile, onFileDelete],
  );

  useEffect(() => {
    const uploadInProgress = Object.values(isUploading).some(
      (value) => value !== "",
    );
    const extractionInProgress = Object.values(isExtracting).some(
      (value) => value,
    );

    const totalFiles = Object.keys({
      ...isUploading,
      ...isExtracting,
      ...uploadedFile,
    }).length;

    const completedFiles = Object.keys(uploadedFile).length;

    if (
      totalFiles > 0 &&
      !uploadInProgress &&
      !extractionInProgress &&
      completedFiles === totalFiles
    ) {
      onAllUploadsComplete?.(Object.values(uploadedFile));
    }
  }, [isUploading, isExtracting, uploadedFile, onAllUploadsComplete]);

  const uploadFileToServer = async (file: File) => {
    let fileId = 0;
    try {
      const response = shouldOnlyGeneratePresignedUrl
        ? await generatePresignedUrlOnly({
            fileName: file.name,
          })
        : await generatePresignedUrl({
            fileName: file.name,
          });
      // export interface PresignedUrlResponseDto {
      //   cdnUrl?: string;
      //   fileName?: string;
      //   presignedUrl?: string;
      //   s3Url?: string;
      // }

      // export interface CreateTeacherDocumentResponseDto {
      //   bucketPath?: string;
      //   contentType?: string;
      //   downloadUrl?: string;
      //   fileName?: string;
      //   id?: number;
      //   presignedUrl?: string;
      //   size?: number;
      //   status?: CreateTeacherDocumentResponseDtoStatus;
      //   uploadedAt?: string;
      //   uploadedBy?: number;
      //   url?: string;
      // }
      const signedUrl = response.presignedUrl;
      let downloadUrl = null;
      if (!shouldOnlyGeneratePresignedUrl && "downloadUrl" in response) {
        downloadUrl = response.downloadUrl;
        if (response.id !== undefined) {
          fileId = response.id;
        } else {
          throw new Error(t("file_uploader.error.uploadFailed"));
        }
      } else if (shouldOnlyGeneratePresignedUrl) {
        //converts char and num combination to number
        fileId =
          parseInt(
            (response as PresignedUrlResponseDto).cdnUrl
              ?.split("/")
              .pop()
              ?.split("-")[0] || "0",
            10,
          ) || 0;
        console.log("fileId", fileId);
      }
      if (!signedUrl) {
        const error = t("file_uploader.error.uploadFailed");
        onUploadError?.(error);
        return;
      }

      onUploadStart?.();

      setIsUploading((prev) => ({ ...prev, [fileId]: file.name }));
      setUploadProgress((prev) => ({ ...prev, [fileId]: 0 }));

      // Use XMLHttpRequest for upload progress tracking
      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhrRefs.current[fileId] = xhr;

        xhr.open("PUT", signedUrl, true);

        xhr.upload.onprogress = (e) => {
          const progress = (e.loaded / e.total) * 100;
          setUploadProgress((prev) => ({
            ...prev,
            [fileId]: Math.round(progress),
          }));
        };

        xhr.onload = async () => {
          delete xhrRefs.current[fileId];
          if (xhr.status === 200) {
            if (shouldExtractContent && fileId) {
              try {
                setIsExtracting((prev) => ({
                  ...prev,
                  [fileId]: true,
                }));
                const response = await extractTeacherDocumentContent(fileId);
                const fileUrl = signedUrl.split("?")[0];
                startPolling(response.id!, fileUrl, resolve);
              } catch (extractError) {
                console.error(
                  "Error extracting document content:",
                  extractError,
                );
                onUploadError?.(t("file_uploader.error.extractContentFailed"));
                reject(t("file_uploader.error.extractContentFailed"));
                return;
              }
            } else {
              const fileUrl = signedUrl.split("?")[0];
              if (shouldOnlyGeneratePresignedUrl) {
                await onUploadSuccess?.(fileUrl, response);
              } else {
                await onUploadSuccess?.(fileUrl, response, downloadUrl!);
              }
              resolve();
            }
          } else {
            reject(new Error("file_uploader.error.uploadFailed"));
          }
        };

        xhr.onerror = () => {
          delete xhrRefs.current[fileId];
          reject(new Error("file_uploader.error.uploadFailed"));
        };

        xhr.onabort = () => {
          delete xhrRefs.current[fileId];
          reject(new Error("file_uploader.error.uploadCancelled"));
        };

        xhr.send(file);
      });
    } catch (error) {
      const errorMessage = t("file_uploader.error.uploadFailed");
      onUploadError?.(errorMessage);
      console.error("Upload error:", error);
    } finally {
      if (fileId > 0) {
        setIsUploading((prev) => {
          const newState = { ...prev };
          delete newState[fileId];
          return newState;
        });
        // Remove the 100% progress set since we'll show complete state differently
        if (!shouldExtractContent) {
          setUploadProgress((prev) => ({
            ...prev,
            [fileId]: 100,
          }));
        }
      }
    }
  };

  const onDrop = useCallback(
    async (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
      if (rejectedFiles.length > 0) {
        if (rejectedFiles[0].errors[0].code === "file-too-large") {
          const error = t("file_uploader.error.fileTooLarge", {
            maxSize: maxSize / (1024 * 1024),
          });
          onUploadError?.(error);
          return;
        }
        const error = t("file_uploader.error.invalidFile");
        onUploadError?.(error);
        return;
      }

      if (!multiple) {
        setUploadedFile({});
        setUploadProgress({});
        setIsUploading({});
        setIsExtracting({});
      }

      // Only process the first file if multiple is false
      const filesToProcess = multiple
        ? acceptedFiles
        : acceptedFiles.slice(0, 1);

      if (filesToProcess.length > 0) {
        onUploadStart?.();
      }

      for (const file of filesToProcess) {
        uploadFileToServer(file);
      }
    },
    [onUploadError, onUploadStart, t, multiple, maxSize],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept,
    maxSize,
    multiple,
  });

  if (small) {
    return (
      <div {...getRootProps()} className="relative">
        <input {...getInputProps()} />
        {Object.values(isUploading).some((value) => value !== "") ||
        Object.values(isExtracting).some((value) => value) ? (
          <Loader2 className="w-5 h-5 animate-spin text-gray-600 dark:text-gray-400" />
        ) : (
          <File className="w-5 h-5 cursor-pointer text-gray-600 dark:text-gray-400" />
        )}
      </div>
    );
  }

  return (
    <div
      {...getRootProps()}
      className={`relative group w-full border-2 border-dashed rounded-xl p-6 flex flex-col justify-center transition-all duration-300 ${
        isDragActive
          ? getColorClasses("drag-active")
          : Object.keys(uploadedFile).length > 0 &&
              Object.keys(uploadedFile).length ===
                Object.keys(uploadProgress).length
            ? getColorClasses("success")
            : getColorClasses("default")
      } ${className}`}
      style={style}
      data-testid="file-uploader-drop-zone"
    >
      <input {...getInputProps()} />

      {/* Upload area */}
      <div className="flex flex-col items-center justify-center space-y-4">
        {!Object.values(isUploading).some((value) => value !== "") &&
        !Object.values(isExtracting).some((value) => value) &&
        !Object.keys(uploadedFile).length ? (
          <>
            <div className="relative">
              <div
                className={`w-20 h-20 rounded-2xl flex items-center justify-center ${
                  isDragActive
                    ? colorMap[uploadColor as ColorVariant].bgLight
                    : "bg-gray-50 dark:bg-gray-800"
                } transition-colors duration-200`}
              >
                <Upload
                  className={`h-10 w-10 ${
                    isDragActive
                      ? colorMap[uploadColor as ColorVariant].text
                      : "text-gray-400 dark:text-gray-500"
                  }`}
                />
              </div>
            </div>
            <div className="text-center space-y-2">
              <Text variant="p" size="sm" className="font-medium">
                {isDragActive
                  ? dragActiveText || t("file_uploader.dropHere")
                  : dragInactiveText || t("file_uploader.dragAndDrop")}
              </Text>
              <Text
                variant="p"
                status="secondary"
                size="xs"
                className="max-w-sm mx-auto"
              >
                {[
                  ...new Set(
                    Object.values(accept)
                      .flat()
                      .map((ext) => ext.toLowerCase()),
                  ),
                ]
                  .join(", ")
                  .toUpperCase()}
                <br />({Math.round(maxSize / (1024 * 1024))}MB max)
                {multiple && (
                  <span> • {t("file_uploader.multipleAllowed")}</span>
                )}
              </Text>
            </div>
          </>
        ) : (
          <div className="w-full text-center mb-4">
            <Text variant="h6" className="font-semibold">
              {Object.values(isUploading).some((value) => value !== "") ||
              Object.values(isExtracting).some((value) => value)
                ? t("file_uploader.processingFiles")
                : t("file_uploader.uploadedFiles")}
            </Text>
            <Text
              variant="p"
              size="sm"
              className="mt-2 text-gray-500 dark:text-gray-400"
            >
              {isDragActive
                ? dragActiveText || t("file_uploader.dropHere")
                : dragInactiveText || t("file_uploader.dragAndDrop")}
            </Text>
          </div>
        )}
      </div>

      {/* Files list */}
      {(Object.values(isUploading).some((value) => value !== "") ||
        Object.values(isExtracting).some((value) => value) ||
        Object.keys(uploadedFile).length > 0) && (
        <div
          className={`mt-6 space-y-3 ${
            Object.entries({ ...isUploading, ...isExtracting, ...uploadedFile })
              .length > 3
              ? "max-h-72 overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600 scrollbar-track-transparent"
              : ""
          }`}
        >
          {Object.entries({
            ...isUploading,
            ...isExtracting,
            ...uploadedFile,
          }).map(([id, _]) => {
            const fileId = Number(id);
            const isUploadingFile = isUploading[fileId];
            const isExtractingFile = isExtracting[fileId];
            const uploadProgressValue = uploadProgress[fileId] || 0;
            const isComplete = uploadedFile[fileId];

            return (
              <div
                key={fileId}
                className={`group flex items-center p-4 rounded-xl border-2 transition-all duration-200 ${
                  isComplete
                    ? `${colorMap[successColor as ColorVariant].border} ${colorMap[successColor as ColorVariant].bg} shadow-sm hover:shadow-md`
                    : isExtractingFile
                      ? `${colorMap[extractColor as ColorVariant].border} ${colorMap[extractColor as ColorVariant].bg} shadow-sm`
                      : `${colorMap[uploadColor as ColorVariant].border} bg-white dark:bg-gray-800 shadow-sm`
                } ${isComplete ? "cursor-pointer hover:scale-[1.01]" : ""}`}
              >
                {/* File Icon / Progress */}
                <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center mr-4">
                  {isUploadingFile !== "" ? (
                    <div className="relative w-10 h-10">
                      <div className="absolute inset-0 rounded-full border-2 border-gray-100 dark:border-gray-700" />
                      <svg
                        className="absolute inset-0 w-full h-full transform -rotate-90"
                        viewBox="0 0 100 100"
                      >
                        <circle
                          className={`transition-all duration-300 ease-in-out ${colorMap[uploadColor as ColorVariant].text}`}
                          cx="50"
                          cy="50"
                          r="48"
                          stroke="currentColor"
                          strokeWidth="4"
                          fill="none"
                          strokeDasharray={`${Number(uploadProgressValue) * 3.14}, 314`}
                          strokeLinecap="round"
                        />
                      </svg>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <Text
                          variant="span"
                          className={`text-xs font-semibold ${colorMap[uploadColor as ColorVariant].text}`}
                        >
                          {Math.round(Number(uploadProgressValue))}%
                        </Text>
                      </div>
                    </div>
                  ) : isExtractingFile ? (
                    <div className="relative">
                      <Loader2
                        className={`w-8 h-8 ${colorMap[extractColor as ColorVariant].text} animate-spin`}
                      />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <FileText
                          className={`w-4 h-4 ${colorMap[extractColor as ColorVariant].text}`}
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="relative">
                      <div
                        className={`w-10 h-10 rounded-full ${colorMap[successColor as ColorVariant].bg} flex items-center justify-center`}
                      >
                        <CheckCircle2
                          className={`w-6 h-6 ${colorMap[successColor as ColorVariant].text}`}
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* File Info */}
                <div
                  className="flex-1 min-w-0"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (isComplete) {
                      window.open(uploadedFile[fileId]?.downloadUrl, "_blank");
                    }
                  }}
                >
                  <Text variant="p" size="sm" className="font-medium truncate">
                    {isUploadingFile !== "" && isUploadingFile !== undefined
                      ? isUploadingFile
                      : uploadedFile[fileId]?.fileName}
                  </Text>
                  <Text
                    variant="span"
                    size="xs"
                    status="secondary"
                    className={`inline-flex items-center ${
                      isComplete
                        ? "text-green-600 dark:text-green-400"
                        : isExtractingFile
                          ? `${colorMap[extractColor as ColorVariant].text}`
                          : ""
                    }`}
                  >
                    {isComplete
                      ? uploadSuccessText || t("file_uploader.uploadSuccess")
                      : isExtractingFile
                        ? extractingContentText ||
                          t("file_uploader.extractingContent")
                        : t("file_uploader.uploading")}
                  </Text>
                </div>

                {/* Action Buttons */}
                <div className="flex-shrink-0 ml-4">
                  {isUploadingFile !== "" ? (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(fileId);
                      }}
                      className={`p-2 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-200 ${colorMap[uploadColor as ColorVariant].text} hover:${colorMap[uploadColor as ColorVariant].bg} hover:scale-110`}
                      title={t("file_uploader.cancelUpload")}
                    >
                      <XCircle className="w-5 h-5" />
                    </button>
                  ) : (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(fileId);
                      }}
                      className="p-2 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-200 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 hover:scale-110"
                      title={t("file_uploader.deleteFile")}
                    >
                      <X className="w-5 h-5" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Show upload area after files if multiple is true */}
      {multiple && Object.keys(uploadedFile).length > 0 && (
        <div className="mt-6 flex items-center justify-center gap-2 p-4 border-2 border-dashed rounded-xl border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 transition-colors">
          <div className="w-12 h-12 rounded-2xl flex items-center justify-center bg-gray-50 dark:bg-gray-800 border-2 border-gray-300 dark:border-gray-600">
            <Upload className="h-8 w-8 text-gray-400 dark:text-gray-500" />
          </div>
          <Text variant="p" size="sm" className="font-medium">
            {isDragActive
              ? dragActiveText || t("file_uploader.dropHere")
              : t("file_uploader.addMoreFiles")}
          </Text>
        </div>
      )}

      {/* Drop overlay */}
      {isDragActive && (
        <div className="absolute inset-0 bg-opacity-50 backdrop-blur-sm bg-white dark:bg-gray-900 dark:bg-opacity-50 flex items-center justify-center rounded-xl">
          <Text
            variant="p"
            size="lg"
            className={`font-medium text-center ${colorMap[uploadColor as ColorVariant].text}`}
          >
            {dragActiveText || t("file_uploader.dropHere")}
          </Text>
        </div>
      )}
    </div>
  );
}
